/**
 * Removes every service worker registered for this origin and empties the caches they
 * filled, then reloads.
 *
 * A service worker outlives the code that installed it: it keeps serving the shell it
 * cached until something unregisters it. While developing that is a trap — the page can
 * keep answering with a build from an hour ago, and a hard refresh does not always get
 * past it. This is the way out, and it is deliberately available from the console rather
 * than hidden in a menu.
 *
 * Exposed on `window` only in development. In production the same call would silently
 * discard whatever a person had cached to read offline.
 */
export async function resetProgressiveWebApplication(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((registration) => registration.unregister()))
    console.info(`pwaReset: unregistered ${registrations.length} service worker(s)`)
  }

  if ('caches' in window) {
    const cacheNames = await caches.keys()
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
    console.info(`pwaReset: deleted ${cacheNames.length} cache(s)`)
  }

  window.location.reload()
}

declare global {
  interface Window {
    pwaReset?: () => Promise<void>
  }
}

export function installPwaResetHelper(): void {
  window.pwaReset = resetProgressiveWebApplication
  console.info('pwaReset() is available: it unregisters service workers, clears caches and reloads.')
}
